/**
 * Carlyle Avenue contact concierge — conditional Order-number reveal
 *                                     + custom-field draft restore
 * -----------------------------------------------------------------------------
 * Drives `sections/ca-contact.liquid`.
 *
 * NOTE ON SUBMIT: the form uses the NATIVE full-page POST. We tried a background
 * (fetch) submit to avoid the reload, but this store's Shopify spam protection
 * requires a captcha token on every contact submission, so a tokenless AJAX POST
 * is always rejected — the reload is a Shopify platform constraint here, not a
 * theme choice. The pop-up is opened at first paint by a small inline script in
 * the section (so it isn't late); `#maybeShowSuccess` below is the fallback.
 *
 * 1. REVEAL — the Order-number field is server-rendered VISIBLE (progressive
 *    enhancement — no-JS shoppers keep the optional field); on connect this
 *    component NARROWS it to only show once an order-type reason is chosen.
 *
 * 2. DRAFT RESTORE — Shopify's native contact form only echoes name/email/body
 *    back on a validation error; the custom `contact[Inquiry type]` /
 *    `contact[Order number]` values are dropped on the error reload, wiping the
 *    shopper's topic + order number. We capture those two on submit into
 *    sessionStorage and restore them if the page comes back rendering an error
 *    (detected by the presence of `.ca-contact__error`). The key is scoped to
 *    the form id so multiple instances never collide. Everything is wrapped in
 *    try/catch — sessionStorage can throw in private mode and must stay optional.
 *
 * All refs live in this element's own subtree; lookups are defensive (no
 * `requiredRefs`) because the select/field are absent on the success takeover
 * and when the merchant clears every reason.
 */
import { Component } from '@theme/component';

/**
 * @typedef {object} CaContactFormRefs
 * @property {HTMLSelectElement} [reasonSelect] - The reason-for-reaching-out select.
 * @property {HTMLElement} [orderField] - The Order-number field wrapper to reveal/hide.
 * @extends Component<CaContactFormRefs>
 */
export class CaContactForm extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#sync();

    const form = this.querySelector('form');
    const errored = this.querySelector('.ca-contact__error') != null;

    if (errored) {
      this.#restoreDraft();
      // Move focus to the error box so AT users are actually alerted. This form does a native
      // full-page POST, so the error is present at parse time — role="alert" does NOT reliably
      // announce content that's already in the DOM on load, but moving focus does. (tabindex="-1"
      // was already on the box for exactly this.)
      this.querySelector('.ca-contact__error')?.focus({ preventScroll: false });
    } else {
      // Non-error load (first visit or a successful submit) — drop any stale draft.
      this.#clearDraft();
    }

    if (form) {
      form.addEventListener('submit', () => this.#saveDraft(), { signal: this.#abortController.signal });
    }

    this.#maybeShowSuccess();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** Re-sync when the reason changes (bound via `on:change` in the template). */
  onReasonChange() {
    this.#sync();
  }

  /** Close the success pop-up (bound to the pop-up's Close button). */
  onCloseDialog() {
    this.refs.successDialog?.close();
  }

  /** Close the success pop-up on a backdrop click (bound to the dialog itself). */
  onDialogClick(event) {
    if (event.target === this.refs.successDialog) this.refs.successDialog.close();
  }

  /**
   * Open the success pop-up when the page comes back posted. Fires when the
   * server rendered the inline success (form.posted_successfully?) OR the
   * redirect carries ?contact_posted=true (fallback if the server gate didn't
   * render). Strips the param so a refresh doesn't re-open the pop-up.
   */
  #maybeShowSuccess() {
    const dialog = this.refs.successDialog;
    // The inline first-paint script may already have opened it — don't double-open.
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;

    const params = new URLSearchParams(window.location.search);
    // `[data-ca-posted]` is the server success marker (the visible confirmation
    // lives in <noscript> now, so it isn't in the DOM for us); the query param is
    // the fallback if the marker didn't render.
    const posted = this.querySelector('[data-ca-posted]') != null || params.has('contact_posted');
    if (!posted) return;

    dialog.showModal();

    if (params.has('contact_posted')) {
      params.delete('contact_posted');
      const query = params.toString();
      const url = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
      window.history.replaceState(null, '', url);
    }
  }

  /** Show the Order-number field only when the selected reason opts into it. */
  #sync() {
    const select = this.refs.reasonSelect;
    const orderField = this.refs.orderField;
    if (!select || !orderField) return;

    const option = select.options[select.selectedIndex];
    const reveal = Boolean(option && option.hasAttribute('data-ca-reveal-order'));
    orderField.hidden = !reveal;
  }

  /** sessionStorage key, scoped to this form so instances don't clobber each other. */
  #draftKey() {
    const form = this.querySelector('form');
    return `ca-contact-draft:${form?.id || 'default'}`;
  }

  /** @returns {HTMLInputElement | null} */
  #orderInput() {
    return this.refs.orderField?.querySelector('input') ?? null;
  }

  /** Persist the two custom fields just before the form POSTs. */
  #saveDraft() {
    try {
      const draft = {
        reason: this.refs.reasonSelect?.value ?? '',
        order: this.#orderInput()?.value ?? '',
      };
      sessionStorage.setItem(this.#draftKey(), JSON.stringify(draft));
    } catch {
      // sessionStorage unavailable (private mode / disabled) — non-fatal.
    }
  }

  /** Restore the custom fields after an error reload, then clear the draft. */
  #restoreDraft() {
    let draft = null;
    try {
      draft = JSON.parse(sessionStorage.getItem(this.#draftKey()) ?? 'null');
    } catch {
      return;
    }
    if (!draft) return;

    const select = this.refs.reasonSelect;
    if (select && draft.reason) {
      const isValid = Array.from(select.options).some((option) => option.value === draft.reason);
      if (isValid) select.value = draft.reason;
    }

    const orderInput = this.#orderInput();
    if (orderInput && draft.order) orderInput.value = draft.order;

    // Re-reveal the Order field if the restored reason is an order-type one.
    this.#sync();
    this.#clearDraft();
  }

  #clearDraft() {
    try {
      sessionStorage.removeItem(this.#draftKey());
    } catch {
      // noop
    }
  }
}

if (!customElements.get('ca-contact-form')) {
  customElements.define('ca-contact-form', CaContactForm);
}
